import {
    type ReactNode,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IBreadcrumbLinkProps from "./type";
import type {
    BreadcrumbLinkNativePropsType,
    BreadcrumbLinkWebPropsType
} from "./type";
import stylesheet, {
    getBreadcrumbLinkColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useBreadcrumbContext
} from "../../context";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    getBreadcrumbMeasures
} from "../../stylesheet";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const BreadcrumbLink: FC<IBreadcrumbLinkProps> = ({
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    icon: IconComponent,
    accessibilityLabel,
    isDisabled = false,
    isIconOnly = false,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    style,
    title,
    href,
    ...props
}) => {
    const context = useBreadcrumbContext();

    const customTheme = customThemeProp ?? context.customTheme;

    const {
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isIconOnlyLink = isIconOnly && !!IconComponent;

    const isInteractive = !isDisabled && !context.isMeasure;

    const measures = getBreadcrumbMeasures({
        size: context.size,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getBreadcrumbLinkColors({
        isHighlighted: isInteractive && (isHovered || isPressed),
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle,
        title: titleDynamicStyle
    } = useStyles({
        pointerType: context.isCompact ? "coarse" : pointerType,
        isFocusVisible: isInteractive && isFocusVisible,
        isIconOnly: isIconOnlyLink,
        shapeConfigs,
        touchTargets,
        measures,
        radiuses,
        borders,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const renderIcon = () => {
        if(!IconComponent) {
            return null;
        }

        return <IconComponent
            customColor={currentColors.iconColor}
            color={currentColors.iconColorKey}
            size={measures.iconSize}
        />;
    };

    const renderTitle = () => {
        if(isIconOnlyLink) {
            return null;
        }

        return <Text
            customColor={currentColors.titleColor}
            variant={measures.linkVariant}
            customTheme={customTheme}
            style={[
                stylesheet.title,
                titleDynamicStyle
            ]}
            numberOfLines={1}
        >
            {title}
        </Text>;
    };

    const renderSurface = (): ReactNode => {
        return <View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {
                isInteractive ?
                    <StateLayer
                        borderRadius={radiuses.actions}
                        customTheme={customTheme}
                        isHovered={isHovered}
                        isPressed={isPressed}
                    />
                :
                    null
            }
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                {renderIcon()}
                {renderTitle()}
            </View>
        </View>;
    };

    const webProps: BreadcrumbLinkWebPropsType = Platform.OS === "web" && href ? {
        href
    } : {};

    const nativeProps: BreadcrumbLinkNativePropsType = Platform.OS === "web" ? {} : {
        accessibilityState: {
            disabled: isDisabled
        }
    };

    if(!isInteractive) {
        return <View
            aria-label={ariaLabel ?? (isIconOnlyLink ? title : undefined)}
            accessibilityLabel={accessibilityLabel}
            aria-disabled={isDisabled}
            style={[
                containerStyle,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            {renderSurface()}
        </View>;
    }

    return <Pressable
        {...props}
        {...webProps}
        {...nativeProps}
        {...interactionHandlers}
        aria-label={ariaLabel ?? (isIconOnlyLink ? title : undefined)}
        accessibilityLabel={accessibilityLabel}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onPress={onPress}
        role="link"
    >
        {renderSurface()}
    </Pressable>;
};
export default BreadcrumbLink;
